import { useState, useRef, useCallback } from "react";
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  useDroppable,
  useDndContext,
} from "@dnd-kit/core";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Package, MousePointer, Settings, Plus, ExpandIcon, ShrinkIcon } from "lucide-react";
import { PackingListPDF } from "./PackingListPDF";
import { VendorPackingListPDF } from "./VendorPackingListPDF";
import { pdf } from "@react-pdf/renderer";
import { GearItemWithPacking } from "@/types/packing";
import { PackingCaseCard } from "./PackingCaseCard";
import { PackingGearItem } from "./PackingGearItem";
import { PackingProgressBar } from "./PackingProgressBar";
import { PackingControls } from "./PackingControls";
import { useToast } from "@/hooks/use-toast";

interface DragDropPackingViewProps {
  items: GearItemWithPacking[];
  cases: any[];
  canEdit: boolean;
  onTogglePacked: (itemId: string, currentStatus: boolean) => void;
  onAssignToCase: (itemId: string, caseName: string | null) => void;
  onDeleteCase: (caseId: string) => void;
  projectName?: string;
  onShowCaseManager?: () => void;
  onShowCreateCase?: () => void;
}

interface DroppableCase {
  id: string;
  name: string;
  color: string;
  items: GearItemWithPacking[];
}

export function DragDropPackingView({
  items,
  cases,
  canEdit,
  onTogglePacked,
  onAssignToCase,
  onDeleteCase,
  projectName = "Project",
  onShowCaseManager = () => {},
  onShowCreateCase = () => {},
}: DragDropPackingViewProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [allCasesCollapsed, setAllCasesCollapsed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Organize items by case
  const assignedCases: DroppableCase[] = cases.map(caseItem => ({
    id: caseItem.id,
    name: caseItem.name,
    color: caseItem.color || "#3b82f6",
    items: items.filter(item => item.assigned_case === caseItem.name),
  }));

  const unassignedItems = items.filter(item => !item.assigned_case);

  const handleDragStart = (event: DragStartEvent) => {
    const itemId = event.active.id as string;
    
    // If the dragged item is not selected, start fresh selection
    if (!selectedItems.has(itemId)) {
      setSelectedItems(new Set([itemId]));
    }
    
    setActiveId(itemId);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!canEdit) {
      setActiveId(null);
      return;
    }

    // If dropped outside any drop zone OR dropped on unassigned area, move to unassigned
    if (!over || over.id === "unassigned") {
      const itemsToMove = selectedItems.size > 0 ? Array.from(selectedItems) : [active.id as string];
      itemsToMove.forEach(itemId => {
        onAssignToCase(itemId, null);
      });
      setActiveId(null);
      setSelectedItems(new Set());
      
      toast({
        title: "Items moved to gear list",
        description: `${itemsToMove.length} item(s) moved to unassigned`,
      });
      return;
    }

    const overId = over.id as string;
    
    // Find the target case
    let targetCaseName: string | null = null;
    if (overId !== "unassigned") {
      const targetCase = cases.find(c => c.id === overId);
      targetCaseName = targetCase?.name || null;
    }
    
    // Move all selected items
    const itemsToMove = selectedItems.size > 0 ? Array.from(selectedItems) : [active.id as string];
    itemsToMove.forEach(itemId => {
      onAssignToCase(itemId, targetCaseName);
    });
    
    // Show feedback toast
    if (targetCaseName) {
      toast({
        title: `Moved to ${targetCaseName}`,
        description: `${itemsToMove.length} item(s) assigned to case`,
      });
    }
    
    setActiveId(null);
    setSelectedItems(new Set());
  };

  const handleItemClick = useCallback((itemId: string, event: React.MouseEvent) => {
    if (!canEdit) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    if (event.ctrlKey || event.metaKey) {
      // Toggle selection
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        if (newSet.has(itemId)) {
          newSet.delete(itemId);
        } else {
          newSet.add(itemId);
        }
        return newSet;
      });
    } else {
      // Single selection or unselect if already selected
      setSelectedItems(prev => {
        if (prev.has(itemId) && prev.size === 1) {
          // If this item is the only selected item, unselect it
          return new Set();
        } else {
          // Otherwise, select only this item
          return new Set([itemId]);
        }
      });
    }
  }, [canEdit]);

  const handleCollapseAll = () => {
    setAllCasesCollapsed(true);
  };

  const handleExpandAll = () => {
    setAllCasesCollapsed(false);
  };

  const activeItem = activeId ? items.find(item => item.id === activeId) : null;
  const selectedCount = selectedItems.size;

  const handleExportPDF = async () => {
    try {
      const doc = <PackingListPDF items={items} cases={cases} projectName={projectName} />;
      const asPdf = pdf(doc);
      const blob = await asPdf.toBlob();
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `${projectName}-packing-list.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handleExportVendorPDF = async () => {
    try {
      const doc = <VendorPackingListPDF items={items} projectName={projectName} />;
      const asPdf = pdf(doc);
      const blob = await asPdf.toBlob();
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `${projectName}-vendor-packing-list.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating vendor PDF:', error);
    }
  };

  const totalItems = items.length;
  const packedItems = items.filter(item => item.is_packed).length;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-3 sm:space-y-4 md:space-y-6">
        {/* Progress and controls section */}
        <div className="sticky top-2 sm:top-4 z-10 flex flex-col gap-3 sm:gap-4">
          <PackingProgressBar 
            packedCount={packedItems}
            totalCount={totalItems}
          />
          
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center sm:justify-between items-center">
            {canEdit && (
              <div className="flex gap-2 w-full sm:w-auto">
                <Button 
                  variant="outline" 
                  onClick={onShowCaseManager}
                  size="sm"
                  className="flex-1 sm:flex-none"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Manage Cases</span>
                  <span className="sm:hidden">Manage</span>
                </Button>
                <Button 
                  onClick={onShowCreateCase}
                  size="sm"
                  className="flex-1 sm:flex-none"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Create Case</span>
                  <span className="sm:hidden">Create</span>
                </Button>
              </div>
            )}
            
            <div className="flex gap-2">
              <Button 
                onClick={allCasesCollapsed ? handleExpandAll : handleCollapseAll}
                variant="ghost"
                size="sm"
                className="gap-1 text-xs md:text-sm h-8"
              >
                {allCasesCollapsed ? (
                  <>
                    <ExpandIcon className="h-3 w-3" />
                    <span className="hidden sm:inline">Expand</span>
                  </>
                ) : (
                  <>
                    <ShrinkIcon className="h-3 w-3" />
                    <span className="hidden sm:inline">Collapse</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Help tooltip for multi-selection */}
        {canEdit && (
          <div className="p-3 sm:p-4 bg-primary/10 rounded-lg text-center text-xs sm:text-sm text-muted-foreground border border-primary/20">
            <MousePointer className="h-3 w-3 sm:h-4 sm:w-4 inline mr-2" />
            <span className="sm:hidden">Tap and hold to drag items between cases</span>
            <span className="hidden sm:inline">Use Ctrl/Cmd+Click to select multiple items, then drag to move them all at once</span>
          </div>
        )}

        {/* Cases section */}
        {assignedCases.length > 0 && (
          <div className="space-y-3 sm:space-y-4">
            <h3 className="text-base sm:text-lg font-semibold px-1">Cases</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              <CasesWithDragState 
                assignedCases={assignedCases}
                canEdit={canEdit}
                onTogglePacked={onTogglePacked}
                onDeleteCase={onDeleteCase}
                selectedItems={selectedItems}
                onItemClick={handleItemClick}
                allCasesCollapsed={allCasesCollapsed}
                activeId={activeId}
              />
            </div>
          </div>
        )}

        {/* Unassigned items section */}
        <UnassignedItemsDropZone
          items={unassignedItems}
          canEdit={canEdit}
          onTogglePacked={onTogglePacked}
          selectedItems={selectedItems}
          onItemClick={handleItemClick}
          containerRef={containerRef}
          activeId={activeId}
        />
      </div>

      <DragOverlay>
        {activeItem && (
          <div className="scale-75">
            <PackingGearItem
              item={activeItem}
              canEdit={canEdit}
              onTogglePacked={onTogglePacked}
            />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

interface CasesWithDragStateProps {
  assignedCases: DroppableCase[];
  canEdit: boolean;
  onTogglePacked: (itemId: string, currentStatus: boolean) => void;
  onDeleteCase: (caseId: string) => void;
  selectedItems: Set<string>;
  onItemClick: (itemId: string, event: React.MouseEvent) => void;
  allCasesCollapsed: boolean;
  activeId: string | null;
}

function CasesWithDragState({
  assignedCases,
  canEdit,
  onTogglePacked,
  onDeleteCase,
  selectedItems,
  onItemClick,
  allCasesCollapsed,
  activeId
}: CasesWithDragStateProps) {
  const { over } = useDndContext();

  return (
    <>
      {assignedCases.map((caseItem) => (
        <PackingCaseCard
          key={caseItem.id}
          id={caseItem.id}
          name={caseItem.name}
          color={caseItem.color}
          items={caseItem.items}
          canEdit={canEdit}
          onTogglePacked={onTogglePacked}
          onDeleteCase={onDeleteCase}
          selectedItems={selectedItems}
          onItemClick={onItemClick}
          isCollapsed={allCasesCollapsed}
          isOver={over?.id === caseItem.id}
          activeId={activeId}
        />
      ))}
    </>
  );
}

interface UnassignedItemsDropZoneProps {
  items: GearItemWithPacking[];
  canEdit: boolean;
  onTogglePacked: (itemId: string, currentStatus: boolean) => void;
  selectedItems: Set<string>;
  onItemClick: (itemId: string, event: React.MouseEvent) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  activeId: string | null;
}

function UnassignedItemsDropZone({
  items,
  canEdit,
  onTogglePacked,
  selectedItems,
  onItemClick,
  containerRef,
  activeId
}: UnassignedItemsDropZoneProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: "unassigned",
  });

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex items-center justify-between px-1">
        <h3 className="text-base sm:text-lg font-semibold">Gear List</h3>
        {selectedItems.size > 0 && (
          <Badge variant="secondary" className="flex items-center gap-1 text-xs">
            <MousePointer className="h-2 w-2" />
            {selectedItems.size} selected
          </Badge>
        )}
      </div>
      <div 
        ref={(el) => {
          setNodeRef(el);
          if (containerRef) {
            (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
          }
        }}
        className={`
          min-h-[160px] sm:min-h-[180px] rounded-lg border border-dashed p-3 sm:p-4 transition-all duration-300
          ${isOver ? 'border-primary/50 bg-primary/5 ring-1 ring-primary/30 scale-[1.01]' : 'border-white/10'}
          bg-white/5 backdrop-blur-sm
        `}
      >
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-24 sm:h-32 text-muted-foreground">
            <Package className="h-8 w-8 sm:h-12 sm:w-12 md:h-16 md:w-16 mb-2 sm:mb-3 md:mb-4 opacity-40" />
            <p className="text-sm sm:text-base md:text-lg font-medium">All items assigned to cases!</p>
            <p className="text-xs sm:text-sm text-center px-2">Drag items from cases back here to unassign them</p>
          </div>
        ) : (
          <div className="grid gap-2">
            {items.map((item) => (
              <PackingGearItem
                key={item.id}
                item={item}
                canEdit={canEdit}
                onTogglePacked={onTogglePacked}
                isSelected={selectedItems.has(item.id)}
                onItemClick={onItemClick}
                isBeingDragged={activeId === item.id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

